import React from 'react';
import CheckmarkIcon from '@shared/icons/CheckmarkIcon';
import ClockIcon from '@shared/icons/ClockIcon';
import SummaryBox from '@/components/SummaryBox';
import CtaButton from '@/components/CtaButton';
import SchoolContactLine from '@/components/SchoolContactLine';
import { appliedToLabel, formatDate, formatMoney, paymentMethodLabel } from '@/format';
import { safeHttpsUrl } from '@/services/links';

/**
 * The post-Checkout confirmation for a payment that went through (`paid`), a
 * bank debit that is on its way (`processing`), or a bank the parent still has
 * to verify before anything moves (`verification_pending`). Everything shown
 * was read back from Stripe by the API; nothing here is taken from the URL.
 *
 * The third state is the one that has to be honest rather than reassuring: a
 * bank added by manual entry needs verifying before the payment starts, and
 * the invoice stays due until it does. Dressing that up as a success is how a
 * parent walks away from a payment that never happens.
 *
 * Stripe decides HOW it is verified, and the copy follows it rather than
 * assuming: `verification.microdeposit_type` is `amounts` for two small
 * deposits whose values are entered, or `descriptor_code` for a single
 * deposit carrying a 6-digit code. Naming the wrong one sends the parent
 * looking for something their statement will not show.
 *
 * `onDone` is optional: the page lives in the app's in-app browser sheet,
 * whose own Done control returns the parent to the app, so by default the
 * screen only says so. A caller that can close the sheet itself (a future
 * universal link) passes `onDone` and gets a button.
 */
const PaymentSuccessScreen = ({ result, onDone }) => {
	const processing = result.state === 'processing';
	const verifying = result.state === 'verification_pending';
	// Both non-paid states use the same pending treatment: blue, never a
	// warning or an error (see the pay-info tokens in tailwind.config.js).
	const pending = processing || verifying;
	const amount = formatMoney(result.amount_cents, result.currency);
	const schoolName = result.school?.name ?? 'your school';
	// Stripe's own expected arrival day when it publishes one, so the copy can
	// name a day instead of a vague wait. Parsed at local midnight on purpose:
	// the API sends a UTC calendar date, and letting it parse as UTC would
	// render the day before for anyone west of the meridian.
	const arrival = result.verification?.arrival_date ? formatDate(`${result.verification.arrival_date}T00:00:00`) : null;
	const when = arrival ? `by ${arrival}` : 'in 1–2 business days';
	// Not every attempt gets a hosted page, and a link we cannot vouch for is
	// no link at all.
	const verificationUrl = safeHttpsUrl(result.verification?.hosted_verification_url);

	const title = (() => {
		if (verifying) return 'Verify your bank to finish';
		if (processing) return 'Payment submitted';
		return 'Payment successful';
	})();
	const body = () => {
		if (verifying) {
			const how =
				result.verification?.microdeposit_type === 'descriptor_code'
					? 'a small deposit with a 6-digit code'
					: 'two small deposits';
			return `Your bank needs to be verified before this payment of ${amount} to ${schoolName} can start, and ${schoolName} will see the invoice as unpaid until it does. Watch for ${how} ${when}, then enter ${result.verification?.microdeposit_type === 'descriptor_code' ? 'the code' : 'the amounts'} to complete the payment.`;
		}
		if (processing) return `Your bank payment to ${schoolName} is on its way. We’ll email your receipt once it clears.`;
		if (result.is_partial) {
			return `A partial payment of ${amount} to ${schoolName} has been received. We’ve emailed your receipt.`;
		}
		return `Your payment to ${schoolName} has been received. We’ve emailed your receipt.`;
	};

	return (
		<div className="flex flex-col items-center gap-[18px] pt-[12px]">
			<div
				className={`flex h-[76px] w-[76px] items-center justify-center rounded-full ${
					pending ? 'bg-pay-info-bg' : 'bg-pay-green-bg'
				}`}
			>
				<div
					className={`flex h-[52px] w-[52px] items-center justify-center rounded-full text-white ${
						pending ? 'bg-pay-info' : 'bg-pay-green'
					}`}
				>
					{pending ? (
						<ClockIcon width={26} height={26} aria-hidden="true" focusable="false" />
					) : (
						<CheckmarkIcon width={26} height={26} aria-hidden="true" focusable="false" />
					)}
				</div>
			</div>

			<h1 className="text-center text-[24px] font-extrabold text-pay-ink">{title}</h1>

			<p className="text-center text-[14px] leading-[21px] text-pay-muted">{body()}</p>

			{pending ? (
				<span className="inline-flex items-center gap-[6px] rounded-full bg-pay-info-bg px-[12px] py-[5px] text-[12px] font-semibold text-pay-info">
					<ClockIcon width={14} height={14} aria-hidden="true" focusable="false" />
					{verifying ? 'Waiting on bank verification' : 'Processing · 2–3 business days'}
				</span>
			) : null}

			<SummaryBox
				rows={[
					{
						label: verifying ? 'Amount to pay' : result.is_partial ? 'Partial payment' : 'Amount paid',
						value: amount,
					},
					{ label: 'Applied to', value: appliedToLabel(result.applied_to) },
					{ label: 'Payment method', value: paymentMethodLabel(result.payment_method) },
					{ label: 'Date', value: formatDate(result.paid_at) },
				]}
			/>

			{verifying && verificationUrl ? (
				<CtaButton onClick={() => window.open(verificationUrl, '_blank', 'noopener')}>
					{result.verification.microdeposit_type === 'descriptor_code' ? 'Enter the code' : 'Enter the deposits'}
				</CtaButton>
			) : null}

			{result.processing_fee_cents > 0 ? (
				<p className="-mt-[8px] text-center text-[12px] text-pay-muted">
					Includes {formatMoney(result.processing_fee_cents, result.currency)} service fee
				</p>
			) : null}

			<p className="text-center text-[13px] text-pay-muted">You can close this window and return to the app.</p>

			{onDone ? (
				<div className="w-full">
					<CtaButton onClick={onDone}>Done</CtaButton>
				</div>
			) : null}

			<SchoolContactLine school={result.school} />
		</div>
	);
};

export default PaymentSuccessScreen;
